/**
 * ContextGridSwitcher — Top-bar dropdown displaying all CLIENT-portal personas
 * (Dirigeant, CISO, DPO, Risk Manager, Compliance Officer, Data&IA Officer,
 *  Collaborateur, Administration) as a grid of icon tiles.
 *
 * Switching context updates `useJourneyStore.activeContext`, which re-filters
 * the sidebar, mega-menu, and project-stack steps; then routes to the
 * context's dashboardRoute.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, ClipboardCheck, AlertTriangle, Shield,
  Scale, Target, TrendingUp, FolderOpen, Crown, ShieldCheck,
  Database, Gavel, Repeat2, Search, BrainCircuit, Building2,
  PanelsTopLeft, FolderKanban, Grid2x2, Calculator,
  Users, Eye, Settings as SettingsIcon, ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useJourneyStore } from '@/stores/useJourneyStore'
import { CONTEXTS } from '@/config/contexts'
import type { ContextId } from '@/types/real-estate'

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard, ClipboardCheck, AlertTriangle, Shield,
  Scale, Target, TrendingUp, FolderOpen, Crown, ShieldCheck,
  Database, Gavel, Repeat2, Search, BrainCircuit, Building2,
  PanelsTopLeft, FolderKanban, Grid2x2, Calculator,
  Users, Eye, Settings: SettingsIcon,
}

const COLOR_BG: Record<string, string> = {
  amber: 'bg-amber-500 text-white',
  red: 'bg-red-500 text-white',
  violet: 'bg-violet-500 text-white',
  orange: 'bg-orange-500 text-white',
  teal: 'bg-teal-500 text-white',
  purple: 'bg-purple-500 text-white',
  blue: 'bg-blue-500 text-white',
  slate: 'bg-slate-500 text-white',
  green: 'bg-green-500 text-white',
  indigo: 'bg-indigo-500 text-white',
  pink: 'bg-pink-500 text-white',
  cyan: 'bg-cyan-500 text-white',
}

function getIcon(name: string): LucideIcon {
  return ICON_MAP[name] ?? LayoutDashboard
}

function getColor(color: string): string {
  return COLOR_BG[color] ?? COLOR_BG.slate
}

interface ContextGridSwitcherProps {
  align?: 'start' | 'center' | 'end'
  className?: string
}

export function ContextGridSwitcher({
  align = 'start',
  className,
}: ContextGridSwitcherProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const activeContext = useJourneyStore((s) => s.activeContext)
  const setContext = useJourneyStore((s) => s.setContext)

  const active = CONTEXTS.find((c) => c.id === activeContext) ?? CONTEXTS[0]
  const ActiveIcon = getIcon(active.icon)
  const activeBg = getColor(active.color)

  const handleSelect = (id: ContextId, dashboardRoute: string) => {
    setContext(id)
    setOpen(false)
    if (dashboardRoute) navigate(dashboardRoute)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'flex items-center gap-2 rounded-full pl-1 pr-3 py-1',
            'border bg-card hover:bg-accent transition-colors',
            'text-[12px] font-semibold whitespace-nowrap shrink-0',
            className,
          )}
        >
          <span className={cn('flex h-7 w-7 items-center justify-center rounded-full shrink-0 shadow-sm', activeBg)}>
            <ActiveIcon className="h-3.5 w-3.5" />
          </span>
          <span className="flex-col items-start leading-tight hidden sm:flex">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Contexte</span>
            <span className="text-[12px] font-bold text-foreground">{active.label}</span>
          </span>
          <ChevronDown
            className={cn('h-3 w-3 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        sideOffset={8}
        className="w-[min(96vw,520px)] p-3 border shadow-2xl"
      >
        <div className="mb-2 px-1 flex items-center justify-between">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground">
            Choisir un contexte
          </h4>
          <span className="text-[10px] text-muted-foreground">
            {CONTEXTS.length} rôles
          </span>
        </div>
        <ul className="grid grid-cols-3 gap-1.5">
          {CONTEXTS.map((ctx) => {
            const isActive = ctx.id === activeContext
            const Icon = getIcon(ctx.icon)
            const bg = getColor(ctx.color)
            return (
              <li key={ctx.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(ctx.id, ctx.dashboardRoute)}
                  title={ctx.description}
                  className={cn(
                    'group relative flex h-full w-full flex-col items-start gap-2 rounded-lg p-2.5',
                    'border transition-all duration-150 text-left',
                    isActive
                      ? 'border-primary/40 bg-primary/5 shadow-sm'
                      : 'border-transparent hover:border-border hover:bg-accent/40',
                  )}
                >
                  <span className={cn('flex h-9 w-9 items-center justify-center rounded-xl shrink-0 shadow-sm', bg)}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex flex-col gap-0.5 min-w-0 w-full">
                    <span
                      className={cn(
                        'text-[12px] font-semibold leading-tight truncate',
                        isActive ? 'text-primary' : 'text-foreground',
                      )}
                    >
                      {ctx.label}
                    </span>
                    <span className="text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                      {ctx.description}
                    </span>
                  </span>
                  {isActive && (
                    <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
